import type { Transform, DimLine } from "../geometry";
import { toSvg } from "../geometry";
import { useHighlight } from "../../../state/highlight";

/** Ölçü çizgileri; `useHighlight.dimKey` ile eşleşen ölçü toprak renginde ve kalın çizilir (panelde odaklanan alan). */
export function Dimensions({ t, dims, showLabels = true }: { t: Transform; dims: DimLine[]; showLabels?: boolean }): JSX.Element {
  const hot = useHighlight((s) => s.dimKey);
  // Alta alınan dar etiketler aynı satırda üst üste binmesin: aynı ölçü çizgisi kotunda ardışık dar etiketler bir alt satıra kayar
  const placedTight: { y: number; x0: number; x1: number; row: number }[] = [];
  return (
    <g className="text-[10px] fill-stone" stroke="#6B7280" strokeWidth={0.8}>
      {dims.map((d) => {
        const vertical = Math.abs(d.a.x - d.b.x) < 1e-9;
        const off = d.offset * t.scale;
        const a = toSvg(t, d.a), b = toSvg(t, d.b);
        const ax = vertical ? a.x + off : a.x, ay = vertical ? a.y : a.y - off;
        const bx = vertical ? b.x + off : b.x, by = vertical ? b.y : b.y - off;
        const mx = (ax + bx) / 2, my = (ay + by) / 2;
        const isHot = hot === d.key;
        // Yatay ölçüde etiket aralığa sığmıyorsa (zincir ölçüdeki dar gövde kalınlığı gibi) çizginin altına alınır → komşu etiketlerle çakışmaz
        const tight = !vertical && d.label.length * 6.2 > Math.abs(bx - ax) - 4;
        let row = 0;
        if (tight) {
          const w = d.label.length * 6.2, x0 = mx - w / 2, x1 = mx + w / 2;
          for (const q of placedTight) if (Math.abs(q.y - my) < 6 && x0 < q.x1 + 4 && x1 > q.x0 - 4) row = Math.max(row, q.row + 1);
          placedTight.push({ y: my, x0, x1, row });
        }
        return (
          <g key={d.key} data-dim={d.key} data-hot={isHot || undefined} stroke={isHot ? "rgb(var(--c-soil))" : undefined} strokeWidth={isHot ? 1.6 : undefined}>
            <line x1={a.x} y1={a.y} x2={ax} y2={ay} strokeDasharray="2 2" />
            <line x1={b.x} y1={b.y} x2={bx} y2={by} strokeDasharray="2 2" />
            <line x1={ax} y1={ay} x2={bx} y2={by} markerStart="url(#arrow)" markerEnd="url(#arrow)" />
            {isHot ? <>
              <circle cx={a.x} cy={a.y} r={3} fill="rgb(var(--c-soil))" stroke="none" />
              <circle cx={b.x} cy={b.y} r={3} fill="rgb(var(--c-soil))" stroke="none" />
            </> : null}
            {showLabels ? <text x={vertical ? mx - 4 : mx} y={vertical ? my : tight ? my + 11 + row * 11 : my - 4} textAnchor={vertical ? "end" : "middle"} dominantBaseline={vertical ? "middle" : "auto"} stroke="none" className={`num ${isHot ? "font-semibold" : ""}`} fill={isHot ? "rgb(var(--c-soil))" : undefined}>{d.label}</text> : null}
          </g>
        );
      })}
    </g>
  );
}
